import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { getAuth, isSuperAdmin } from '../../lib/auth';
import { MainLayout } from '../../components/MainLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../../components/ui/sheet';
import ValidationsTab from './tabs/ValidationsTab';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { RefundCheckboxSection, buildRefundPayload } from '../../components/payments/RefundCheckboxSection';
import OverpaymentWarning from '../../components/payments/OverpaymentWarning';
import PaidBySelect from '../../components/payments/PaidBySelect';
import AmountWithBreakdown from '../../components/payments/AmountWithBreakdown';
import useBillPaymentBreakdown from '../../components/payments/useBillPaymentBreakdown';
import MultiRefundDisplay from '../../components/payments/MultiRefundDisplay';
import ZeroPaidCheckbox from '../../components/payments/ZeroPaidCheckbox';
import {
  ArrowLeft, FileText, DollarSign, Zap, Droplets, Flame,
  Clock, CheckCircle2, AlertTriangle, BarChart3, List, Receipt,
  Download, Eye, Plus, Loader2, Trash2, RotateCcw,
  ShieldCheck, Gauge,
} from 'lucide-react';

const fmt = (v) => {
  if (v == null) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
};

const fmtDate = (d) => {
  if (!d) return '\u2014';
  try { return format(new Date(d), 'MMM d, yyyy'); } catch { return d; }
};

const COMMODITY_ICONS = { Electricity: Zap, Gas: Flame, Water: Droplets, Sewer: Droplets };

const TABS = [
  { id: 'statement', label: 'Statement', icon: Receipt },
  { id: 'meters', label: 'Meters', icon: BarChart3 },
  { id: 'usage', label: 'Usage & Demand', icon: Gauge },
  { id: 'charges', label: 'Charges', icon: List },
  { id: 'transactions', label: 'Payments & Refunds', icon: DollarSign },
  { id: 'validations', label: 'Validations', icon: ShieldCheck },
];

export default function BillViewPage() {
  const { billId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const accountId = searchParams.get('account_id');
  const { user } = getAuth();

  const [bill, setBill] = useState(null);
  const [chargesSummary, setChargesSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    return TABS.some((x) => x.id === t) ? t : 'statement';
  });
  const [pdfData, setPdfData] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [payDrawerOpen, setPayDrawerOpen] = useState(false);
  const [refundSection, setRefundSection] = useState({ enabled: false, refund_amount: '', refund_to: '', notes: '' });
  const [payForm, setPayForm] = useState({ transaction_reference: '', amount: '', payment_service_charge: '', notes: '', transaction_date: new Date().toISOString().split('T')[0], paid_by_name: '' });
  const [paySaving, setPaySaving] = useState(false);
  const [relatedBills, setRelatedBills] = useState([]);
  const [relatedPdfDrawer, setRelatedPdfDrawer] = useState({ open: false, billId: null, data: null, loading: false });
  // $0-paid mode (no payment needed) — matches the operator R1 pattern
  const [zeroPaid, setZeroPaid] = useState(false);
  const [zeroPaidReason, setZeroPaidReason] = useState('');

  // Per-bill paid/refund breakdown for tooltip + dot indicator
  const { get: getBreakdown } = useBillPaymentBreakdown(
    billId ? [billId] : [],
    accountId,
  );
  const breakdown = getBreakdown(billId);

  const fetchBill = useCallback(async () => {
    if (!billId || !accountId) return;
    setLoading(true);
    try {
      const [billRes, chargesRes] = await Promise.all([
        api.get(`/bills/${billId}`, { params: { account_id: accountId } }),
        api.get(`/bills/${billId}/charges-summary`, { params: { account_id: accountId } }),
      ]);
      setBill(billRes.data);
      setChargesSummary(chargesRes.data);
    } catch {
      toast.error('Failed to load bill');
    } finally {
      setLoading(false);
    }
  }, [billId, accountId]);

  useEffect(() => { fetchBill(); }, [fetchBill]);

  // Load related bills
  useEffect(() => {
    if (!billId || !accountId) return;
    api.get(`/bills/${billId}/related`, { params: { account_id: accountId } })
      .then(({ data }) => setRelatedBills(data.related_bills || []))
      .catch(() => {});
  }, [billId, accountId]);

  // Load PDF
  const loadPdf = async () => {
    if (pdfData || pdfLoading) return;
    setPdfLoading(true);
    try {
      const { data } = await api.get(`/payments/v2/bill-pdf/${billId}`, { params: { account_id: accountId } });
      setPdfData(data);
    } catch {
      setPdfData({ pdf_url: null, message: 'PDF not available' });
    } finally {
      setPdfLoading(false);
    }
  };

  useEffect(() => { if (billId && accountId) loadPdf(); }, [billId, accountId]);

  const handleDownloadPdf = () => {
    if (pdfData?.base64_data) {
      const byteChars = atob(pdfData.base64_data);
      const byteArr = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
      const blob = new Blob([byteArr], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = pdfData.filename || `bill_${billId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } else if (pdfData?.pdf_url) {
      window.open(pdfData.pdf_url, '_blank');
    }
  };

  // Open related bill PDF in drawer
  const openRelatedPdf = async (relBillId) => {
    setRelatedPdfDrawer({ open: true, billId: relBillId, data: null, loading: true });
    try {
      const { data } = await api.get(`/payments/v2/bill-pdf/${relBillId}`, { params: { account_id: accountId } });
      setRelatedPdfDrawer(prev => ({ ...prev, data, loading: false }));
    } catch {
      setRelatedPdfDrawer(prev => ({ ...prev, data: { pdf_url: null }, loading: false }));
    }
  };

  // Payment recording
  const openPayDrawer = () => {
    const remaining = bill?.remaining != null
      ? bill.remaining
      : Math.max(0, (bill?.total_amount_due || 0) - (bill?.paid_amount || 0));
    setPayForm({
      transaction_reference: '',
      amount: remaining > 0 ? Number(remaining).toFixed(2) : '',
      payment_service_charge: '',
      notes: '',
      transaction_date: new Date().toISOString().split('T')[0],
      paid_by_name: 'BillPortal',
    });
    setPayDrawerOpen(true);
    setRefundSection({ enabled: false, refund_amount: '', refund_to: '', notes: '' });
    setZeroPaid(false);
    setZeroPaidReason('');
  };

  const handleRecordPayment = async () => {
    // Zero-paid path: mark bill paid with $0, no txn insert.
    if (zeroPaid) {
      if (!bill?.payment_file_id) {
        toast.error('Cannot mark $0 paid: bill is not in a Payment File');
        return;
      }
      setPaySaving(true);
      try {
        await api.post(
          `/payments/v2/files/${bill.payment_file_id}/mark-paid-zero`,
          null,
          { params: {
              bill_id: billId,
              account_id: accountId,
              reason: zeroPaidReason || 'No payment needed ($0 due or credits cover it)',
              user_id: user?.id || '',
              user_name: user?.name || '',
            },
          },
        );
        toast.success('Bill marked as paid ($0 — no transaction)');
        setPayDrawerOpen(false);
        setZeroPaid(false);
        setZeroPaidReason('');
        await fetchBill();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to mark bill paid ($0)');
      } finally {
        setPaySaving(false);
      }
      return;
    }

    if (!payForm.transaction_reference?.trim()) { toast.error('Transaction reference required'); return; }
    if (!payForm.amount || parseFloat(payForm.amount) <= 0) { toast.error('Valid amount required'); return; }
    // Full-payment warning — use fresh data from /bills endpoint
    if (bill?.is_fully_paid) {
      const ok = window.confirm(
        `This bill is already fully paid (${(bill.txn_sum || 0).toFixed(2)} of ${(bill.total_amount_due || 0).toFixed(2)} via ${bill.transactions?.length || 0} transaction(s)).\n\nAre you sure you want to record ANOTHER transaction?`
      );
      if (!ok) return;
    }
    setPaySaving(true);
    try {
      await api.post('/bills/record-payment', {
        bill_id: billId,
        account_id: accountId,
        transaction_reference: payForm.transaction_reference,
        amount: parseFloat(payForm.amount),
        payment_service_charge: parseFloat(payForm.payment_service_charge || 0),
        notes: payForm.notes,
        transaction_date: payForm.transaction_date,
        paid_by_name: payForm.paid_by_name || 'BillPortal',
        paid_by: user?.id || '',
      });

      // Chained refund: when the user ticked "Make a refund for this", record
      // a refund tied to this bill through the same Payment-File endpoint
      // used by the dedicated Refunds drawer.
      if (refundSection?.enabled && bill?.payment_file_id) {
        try {
          // Resolve the utility_id for this bill from its PF
          const utilRes = await api.get(`/payments/v2/files/${bill.payment_file_id}/utilities`, {
            params: { account_id: accountId, account_number: bill.account_number, limit: 10 },
          });
          const matchUtil = (utilRes.data?.utilities || []).find(u => u.bill_id === billId);
          if (!matchUtil) {
            toast.error('Payment saved, but refund failed: utility line not found');
          } else {
            const refundPayload = buildRefundPayload({
              value: refundSection,
              accountId,
              utilityId: matchUtil.payment_file_utility_id,
              userId: user?.id || '',
              userName: user?.name || '',
            });
            if (refundPayload) {
              try {
                await api.post(`/payments/v2/files/${bill.payment_file_id}/record-refund`, refundPayload);
                toast.success('Payment recorded · Refund logged');
              } catch (rerr) {
                toast.error('Payment saved, but refund failed: ' + (rerr?.response?.data?.detail || 'Unknown error'));
              }
            }
          }
        } catch (uerr) {
          toast.error('Payment saved, but refund failed: ' + (uerr?.response?.data?.detail || 'utility lookup failed'));
        }
      } else if (refundSection?.enabled) {
        toast.warning('Payment recorded · Refund skipped (bill is not in a Payment File)');
      } else {
        toast.success('Payment recorded');
      }
      setRefundSection({ enabled: false, refund_amount: '', refund_to: '', notes: '' });
      setPayDrawerOpen(false);
      fetchBill();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setPaySaving(false);
    }
  };

  if (loading) {
    return (
      <MainLayout wide>
        <div className="space-y-4 p-6"><Skeleton className="h-10 w-64" /><Skeleton className="h-96 w-full" /></div>
      </MainLayout>
    );
  }

  if (!bill) {
    return (
      <MainLayout wide>
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
          <FileText className="h-12 w-12 mb-3 opacity-40" /><p>Bill not found</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </MainLayout>
    );
  }

  const CommodityIcon = COMMODITY_ICONS[bill.commodity] || FileText;
  const hasPdf = pdfData && (pdfData.base64_data || pdfData.pdf_url);
  const isPaid = bill.payment_status === 'paid' || (bill.paid_amount && bill.paid_amount > 0);

  return (
    <MainLayout wide>
      <TooltipProvider delayDuration={200}>
      <div className="space-y-5" data-testid="bill-view-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="bill-back">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <CommodityIcon className="h-5 w-5 text-foreground" />
            <h1 className="text-xl font-bold">{bill.vendor_name}</h1>
            <Badge variant="outline" className="text-xs">{bill.commodity || 'Utility'}</Badge>
            {bill.pf_batch_name && (
              <>
                <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20 text-xs">{bill.pf_batch_name}</Badge>
                {bill.pf_status && (
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${
                      bill.pf_status === 'review' ? 'bg-amber-500/10 text-amber-700 border-amber-500/30' :
                      bill.pf_status === 'approved' ? 'bg-sky-500/10 text-sky-700 border-sky-500/30' :
                      bill.pf_status === 'paid' ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30' :
                      'bg-slate-500/10 text-slate-600 border-slate-500/30'
                    }`}
                    data-testid="header-pf-status"
                  >
                    {String(bill.pf_status).charAt(0).toUpperCase() + String(bill.pf_status).slice(1)}
                  </Badge>
                )}
              </>
            )}
            {bill.is_consolidated && <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600">Consolidated</Badge>}
            {/* Refund badge — shows when this bill has a refund recorded */}
            {(bill.refund === 'yes' || (bill.refund_amount && Number(bill.refund_amount) > 0)) && (
              <Badge
                variant="outline"
                className={`text-xs gap-1 ${bill.refund_completed ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30' : 'bg-amber-500/10 text-amber-700 border-amber-500/30'}`}
                data-testid="bill-refund-badge"
              >
                <RotateCcw className="h-3 w-3" />
                {bill.refund_completed ? 'Refunded' : 'Refund pending'} to {bill.refund_to || 'Customer'}
                <span className="font-mono ml-1">{fmt(bill.refund_amount)}</span>
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasPdf && (
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={handleDownloadPdf} data-testid="bill-download-pdf">
                <Download className="h-3.5 w-3.5" /> Download PDF
              </Button>
            )}
            <Button size="sm" className="h-8 text-xs gap-1.5" onClick={openPayDrawer} data-testid="bill-pay-btn">
              <DollarSign className="h-3.5 w-3.5" /> {isPaid ? 'Add Payment' : 'Record Payment'}
            </Button>
          </div>
        </div>

        {/* Two-column layout: PDF left, details right */}
        <div className="grid grid-cols-12 gap-5">
          {/* Left: PDF Viewer */}
          <div className="col-span-5 space-y-4">
            <div className="border rounded-lg bg-card overflow-hidden" style={{ minHeight: 500 }} data-testid="bill-pdf-viewer">
              {pdfLoading ? (
                <div className="flex items-center justify-center h-96"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : hasPdf ? (
                pdfData.base64_data ? (
                  <iframe
                    src={`data:application/pdf;base64,${pdfData.base64_data}`}
                    className="w-full border-0"
                    style={{ height: 600 }}
                    title="Bill PDF"
                  />
                ) : (
                  <iframe src={pdfData.pdf_url} className="w-full border-0" style={{ height: 600 }} title="Bill PDF" />
                )
              ) : (
                <div className="flex flex-col items-center justify-center h-96 text-muted-foreground">
                  <FileText className="h-12 w-12 mb-3 opacity-40" />
                  <p className="text-sm">PDF not available</p>
                  <p className="text-xs mt-1">Bill document may not be uploaded yet</p>
                </div>
              )}
            </div>

            {/* Payment Status Card */}
            <div className="border rounded-lg p-4 bg-card space-y-2" data-testid="bill-payment-status">
              <h3 className="text-sm font-semibold">Payment Status</h3>
              {isPaid ? (
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-emerald-700">Paid</p>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <span>Amount:</span>
                      <AmountWithBreakdown
                        amount={breakdown.paid_total > 0 ? breakdown.paid_total : (bill.paid_amount || 0)}
                        breakdown={breakdown.paid_breakdown}
                        count={breakdown.paid_count}
                        label="Paid"
                        testId="bill-detail-paid-breakdown"
                        amountClassName="text-emerald-700 font-semibold"
                      />
                    </div>
                    {bill.transaction_reference && <p className="text-xs text-muted-foreground font-mono mt-0.5">Ref: {bill.transaction_reference}</p>}
                  </div>
                </div>
              ) : bill.refund && ['yes', 'Yes', 'YES'].includes(bill.refund) ? (
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-red-700">Refunded</p>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <span>Amount:</span>
                      <AmountWithBreakdown
                        amount={breakdown.refund_total > 0 ? breakdown.refund_total : (bill.refund_amount || 0)}
                        breakdown={breakdown.refund_breakdown}
                        count={breakdown.refund_count}
                        label="Refund"
                        testId="bill-detail-refund-breakdown"
                        amountClassName="text-red-700 font-semibold"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Unpaid — {fmt(bill.total_amount_due)} due</p>
                </div>
              )}
            </div>

            {/* Multi-refund history (bill.refund + pm_refund_events) */}
            <MultiRefundDisplay
              billId={billId}
              accountId={accountId}
              primaryRefund={bill?.refund === 'yes' || Number(bill?.refund_amount || 0) > 0.01 ? {
                refund_amount: bill?.refund_amount,
                refund_to: bill?.refund_to,
                refund_completed: !!bill?.refund_completed,
                refund_date: bill?.refund_date,
              } : null}
              refreshKey={bill?.updated_at || billId}
            />
          </div>

          {/* Right: Details + Tabs */}
          <div className="col-span-7">
            {/* Tab nav */}
            <div className="border-b border-border mb-4" data-testid="bill-tabs">
              <div className="flex gap-0">
                {TABS.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id)}
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === id ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid={`bill-tab-${id}`}
                  >
                    <Icon className="h-3.5 w-3.5" />{label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab content */}
            <div className="border rounded-lg bg-card overflow-hidden" data-testid={`bill-tab-content-${activeTab}`}>
              {activeTab === 'statement' && <StatementTab bill={bill} relatedBills={relatedBills} onOpenPdf={openRelatedPdf} />}
              {activeTab === 'meters' && <MetersTab identifiers={bill.identifiers || []} />}
              {activeTab === 'usage' && <UsageDemandTab bill={bill} />}
              {activeTab === 'charges' && <ChargesTab summary={chargesSummary} />}
              {activeTab === 'transactions' && <TransactionsTab billId={billId} accountId={accountId} />}
              {activeTab === 'validations' && <ValidationsTab billId={billId} accountId={accountId} />}
            </div>
          </div>
        </div>

        {/* Related Bill PDF Drawer (slides from left) */}
        <Sheet open={relatedPdfDrawer.open} onOpenChange={(o) => setRelatedPdfDrawer(prev => ({ ...prev, open: o }))}>
          <SheetContent side="left" className="w-[600px] sm:w-[700px] p-0" data-testid="related-pdf-drawer">
            <SheetHeader className="px-4 py-3 border-b">
              <SheetTitle className="text-sm">Bill PDF Viewer</SheetTitle>
            </SheetHeader>
            <div className="h-[calc(100vh-60px)]">
              {relatedPdfDrawer.loading ? (
                <div className="flex items-center justify-center h-full"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : relatedPdfDrawer.data?.base64_data ? (
                <iframe
                  src={`data:application/pdf;base64,${relatedPdfDrawer.data.base64_data}`}
                  className="w-full h-full border-0"
                  title="Related Bill PDF"
                />
              ) : relatedPdfDrawer.data?.pdf_url ? (
                <iframe src={relatedPdfDrawer.data.pdf_url} className="w-full h-full border-0" title="Related Bill PDF" />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                  <FileText className="h-12 w-12 mb-3 opacity-40" />
                  <p className="text-sm">PDF not available</p>
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>

        {/* Payment Drawer */}
        <Sheet open={payDrawerOpen} onOpenChange={setPayDrawerOpen}>
          <SheetContent className="w-[420px] sm:w-[480px]" data-testid="pay-drawer">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" /> Record Payment
              </SheetTitle>
            </SheetHeader>
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-muted/30 rounded-lg text-xs space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Vendor</span><span className="font-medium">{bill.vendor_name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Due</span><span className="font-mono font-medium">{fmt(bill.total_amount_due)}</span></div>
                {bill.paid_amount > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Already Paid</span><span className="font-mono text-emerald-600">{fmt(bill.paid_amount)}</span></div>}
              </div>

              {/* $0 paid (no payment needed) toggle — operator R1 pattern */}
              <ZeroPaidCheckbox
                checked={zeroPaid}
                onChange={(v) => {
                  setZeroPaid(v);
                  if (v) {
                    setPayForm(p => ({ ...p, amount: '0', transaction_reference: '$0_due' }));
                  }
                }}
                reason={zeroPaidReason}
                onReasonChange={setZeroPaidReason}
                testId="billview-zero-paid"
              />

              <div className={`space-y-4 ${zeroPaid ? 'opacity-50 pointer-events-none' : ''}`}>
              <div className="space-y-1">
                <Label className="text-xs">Transaction Reference *</Label>
                <Input className="h-9 text-sm" value={payForm.transaction_reference} onChange={e => setPayForm(p => ({ ...p, transaction_reference: e.target.value }))} placeholder="e.g. CHK-001234" data-testid="pay-txn-ref" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Amount *</Label>
                  <Input type="number" step="0.01" className="h-9 text-sm font-mono" value={payForm.amount} onChange={e => setPayForm(p => ({ ...p, amount: e.target.value }))} data-testid="pay-amount" />
                  <OverpaymentWarning
                    paidAmount={payForm.amount}
                    requestedAmount={bill?.payment_amount || bill?.total_amount_due}
                    existingPaid={breakdown.paid_total || bill?.paid_amount || 0}
                    testId="billview-pay-overpay-warning"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Service Fee</Label>
                  <Input type="number" step="0.01" className="h-9 text-sm font-mono" value={payForm.payment_service_charge} onChange={e => setPayForm(p => ({ ...p, payment_service_charge: e.target.value }))} data-testid="pay-fee" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Payment Date</Label>
                  <Input type="date" className="h-9 text-sm" value={payForm.transaction_date} onChange={e => setPayForm(p => ({ ...p, transaction_date: e.target.value }))} data-testid="pay-date" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Paid By *</Label>
                  <PaidBySelect
                    value={payForm.paid_by_name || 'BillPortal'}
                    onChange={v => setPayForm(p => ({ ...p, paid_by_name: v }))}
                    partnerName={bill?.partner_name}
                    customerLabel={bill?.customer_name || ''}
                    testId="billview-pay-paid-by"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Notes</Label>
                <Input className="h-9 text-sm" value={payForm.notes} onChange={e => setPayForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes" data-testid="pay-notes" />
              </div>
              <RefundCheckboxSection
                paidByName={payForm.paid_by_name || 'BillPortal'}
                paidAmount={payForm.amount}
                requestedAmount={bill?.payment_amount || bill?.total_amount_due}
                value={refundSection}
                onChange={setRefundSection}
              />
              </div>
              <Button className="w-full" onClick={handleRecordPayment} disabled={paySaving} data-testid="pay-submit">
                {paySaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Recording...</>
                  : zeroPaid ? 'Mark Paid ($0 — no payment needed)'
                  : `Record ${fmt(parseFloat(payForm.amount) || 0)} Payment`}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
      </TooltipProvider>
    </MainLayout>
  );
}

/* ───── Helpers ───────────────────────── */
const Row = ({ label, value, mono, bold, className = '' }) => (
  <div className={`flex justify-between py-1 ${className}`}>
    <span className="text-muted-foreground">{label}</span>
    <span className={`${mono ? 'font-mono' : ''} ${bold ? 'font-semibold' : ''}`}>{value}</span>
  </div>
);

/* ───── Statement Tab ────────────────── */
const StatementTab = ({ bill, relatedBills, onOpenPdf }) => (
  <div className="p-5 space-y-5">
    {/* 3-column header layout */}
    <div className="grid grid-cols-3 gap-6">
      {/* Col 1: Customer, Address, Payment Status, PF Badge */}
      <div className="space-y-2">
        <h4 className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Customer</h4>
        <p className="text-sm font-medium">{bill.customer_full_name || bill.customer_name}</p>
        <p className="text-xs text-muted-foreground">
          {bill.primary_service_address}
        </p>
        <p className="text-xs text-muted-foreground">
          {bill.primary_service_city}{bill.primary_service_state ? `, ${bill.primary_service_state}` : ''} {bill.primary_service_zip}
        </p>
        <div className="flex items-center gap-2 pt-1">
          {bill.payment_status === 'paid' || (bill.paid_amount > 0) ? (
            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-[10px]">Paid</Badge>
          ) : bill.refund && ['yes','Yes','YES'].includes(bill.refund) ? (
            <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20 text-[10px]">Refund</Badge>
          ) : (
            <Badge variant="outline" className="bg-slate-500/10 text-slate-500 border-slate-500/20 text-[10px]">Unpaid</Badge>
          )}
          {bill.pf_batch_name && (
            <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20 text-[10px]">{bill.pf_batch_name}</Badge>
          )}
          {bill.pf_status && (
            <Badge
              variant="outline"
              className={`text-[10px] ${
                bill.pf_status === 'review' ? 'bg-amber-500/10 text-amber-700 border-amber-500/30' :
                bill.pf_status === 'approved' ? 'bg-sky-500/10 text-sky-700 border-sky-500/30' :
                bill.pf_status === 'paid' ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30' :
                'bg-slate-500/10 text-slate-600 border-slate-500/30'
              }`}
            >
              {String(bill.pf_status).charAt(0).toUpperCase() + String(bill.pf_status).slice(1)}
            </Badge>
          )}
          {bill.is_consolidated && <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-600">Consolidated</Badge>}
        </div>
      </div>

      {/* Col 2: Vendor, Account, Commodities, Invoice */}
      <div className="space-y-2">
        <h4 className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Vendor</h4>
        <p className="text-sm font-medium">{bill.vendor_name}</p>
        <div className="space-y-1 text-xs text-muted-foreground">
          <p><span className="text-muted-foreground/70">Account:</span> <span className="font-mono">{bill.account_number}</span></p>
          <p><span className="text-muted-foreground/70">Commodity:</span> {(bill.commodities && bill.commodities.length > 1) ? bill.commodities.join(', ') : bill.commodity}</p>
          {bill.invoice_number && <p><span className="text-muted-foreground/70">Invoice:</span> <span className="font-mono">{bill.invoice_number}</span></p>}
        </div>
      </div>

      {/* Col 3: All dates */}
      <div className="space-y-2">
        <h4 className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Dates</h4>
        <div className="space-y-1 text-xs">
          <Row label="Invoice Date" value={fmtDate(bill.invoice_date)} />
          <Row label="Due Date" value={fmtDate(bill.due_date)} />
          <Row label="Bill Period" value={bill.period_from && bill.period_to ? `${fmtDate(bill.period_from)} to ${fmtDate(bill.period_to)}` : '\u2014'} />
          <Row label="Parsed Date" value={fmtDate(bill.parsed_date)} />
        </div>
      </div>
    </div>

    {/* Financial breakdown */}
    <div className="pt-3 border-t">
      <h4 className="text-xs font-semibold mb-2">Financial Summary</h4>
      <Table>
        <TableBody>
          {[
            ['Previous Bill Amount', bill.previous_bill_amount],
            ['Previous Payment', bill.previous_bill_payment],
            ['Opening Balance (BBF)', bill.opening_balance],
            ['Current Bill Amount (New Charges)', bill.current_bill_amount],
            ['Discount', bill.discount_amount],
            ['Solar Rebate', bill.solar_rebate],
            ['Late Payment Fee', bill.late_payment_fee],
            ['Adjustments', bill.adjustments],
            ['Tax/GST', bill.gst],
          ].filter(([, v]) => v !== 0 && v != null).map(([label, val]) => (
            <TableRow key={label}>
              <TableCell className="text-xs py-1.5">{label}</TableCell>
              <TableCell className="text-xs py-1.5 text-right font-mono">{fmt(val)}</TableCell>
            </TableRow>
          ))}
          <TableRow className="bg-muted/30 font-semibold">
            <TableCell className="text-xs py-2">Total Amount Due</TableCell>
            <TableCell className="text-xs py-2 text-right font-mono">{fmt(bill.total_amount_due)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>

    {/* Related Bills */}
    {relatedBills && relatedBills.length > 0 && (
      <div className="pt-3 border-t">
        <h4 className="text-xs font-semibold mb-2">Related Bills — {bill.vendor_name} / {bill.account_number}</h4>
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30">
              <TableHead className="text-[10px] font-semibold uppercase">Issue Date</TableHead>
              <TableHead className="text-[10px] font-semibold uppercase">Due Date</TableHead>
              <TableHead className="text-[10px] font-semibold uppercase text-right">New Charges</TableHead>
              <TableHead className="text-[10px] font-semibold uppercase text-right">Total Due</TableHead>
              <TableHead className="text-[10px] font-semibold uppercase">Status</TableHead>
              <TableHead className="text-[10px] font-semibold uppercase">PF Batch</TableHead>
              <TableHead className="text-[10px] font-semibold uppercase w-[60px]">PDF</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {relatedBills.map(rb => (
              <TableRow key={rb.bill_id} className="hover:bg-muted/50">
                <TableCell className="text-xs">{fmtDate(rb.invoice_date)}</TableCell>
                <TableCell className="text-xs">{fmtDate(rb.due_date)}</TableCell>
                <TableCell className="text-xs text-right font-mono">{fmt(rb.current_bill_amount)}</TableCell>
                <TableCell className="text-xs text-right font-mono font-medium">{fmt(rb.total_amount_due)}</TableCell>
                <TableCell>
                  {rb.payment_status === 'paid' || (rb.paid_amount > 0) ? (
                    <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-[10px]">Paid</Badge>
                  ) : (
                    <Badge variant="outline" className="bg-slate-500/10 text-slate-500 border-slate-500/20 text-[10px]">Unpaid</Badge>
                  )}
                </TableCell>
                <TableCell className="text-xs">{rb.pf_batch_name || '\u2014'}</TableCell>
                <TableCell>
                  <button
                    className="p-1 rounded hover:bg-blue-100 transition-colors"
                    onClick={() => onOpenPdf(rb.bill_id)}
                    title="View PDF"
                    data-testid={`related-pdf-${rb.bill_id}`}
                  >
                    <Eye className="h-3.5 w-3.5 text-blue-600" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )}
  </div>
);

/* ───── Meters Tab ───────────────────── */
const MetersTab = ({ identifiers }) => {
  if (!identifiers.length) return <div className="p-8 text-center text-sm text-muted-foreground">No meter data available</div>;
  return (
    <div className="p-4">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30">
            <TableHead className="text-[10px] font-semibold uppercase">Account / Meter</TableHead>
            <TableHead className="text-[10px] font-semibold uppercase">Commodity</TableHead>
            <TableHead className="text-[10px] font-semibold uppercase">Address</TableHead>
            <TableHead className="text-[10px] font-semibold uppercase">Period</TableHead>
            <TableHead className="text-[10px] font-semibold uppercase text-right">Usage</TableHead>
            <TableHead className="text-[10px] font-semibold uppercase text-right">Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {identifiers.map((ident, i) => (
            <TableRow key={i}>
              <TableCell className="text-xs font-mono">{ident.account_number}</TableCell>
              <TableCell className="text-xs">{ident.utility_type}</TableCell>
              <TableCell className="text-xs max-w-[200px] truncate" title={ident.site_address}>{ident.site_address}, {ident.site_city} {ident.site_state}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{ident.period_from && ident.period_to ? `${fmtDate(ident.period_from)} — ${fmtDate(ident.period_to)}` : '\u2014'}</TableCell>
              <TableCell className="text-xs text-right font-mono">{ident.service_usage > 0 ? ident.service_usage.toLocaleString() : '\u2014'}</TableCell>
              <TableCell className="text-xs text-right font-mono font-medium">{fmt(ident.service_total)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

/* ───── Usage & Demand Tab ───────────── */
const USAGE_UOMS = ['kwh', 'ccf', 'therm', 'therms', 'mcf', 'gallon', 'gallons', 'gal', 'kgal', 'hcf', 'cf', 'mwh', 'dth'];
const DEMAND_UOMS = ['kw', 'kva', 'kw-mo', 'kvar', 'mw'];

const classifyLine = (ch) => {
  const uom = (ch.quantity_uom || '').toLowerCase().trim();
  const text = `${ch.category || ''} ${ch.line_item || ''} ${ch.description || ''}`.toLowerCase();
  if (DEMAND_UOMS.includes(uom) || text.includes('demand')) return 'demand';
  if (USAGE_UOMS.includes(uom) || text.includes('usage') || text.includes('consumption')
      || /\bkwh\b|\bccf\b|\btherm/.test(text)) return 'usage';
  return null;
};

const fmtQty = (q, uom) => {
  if (q == null || q === 0) return '\u2014';
  return `${Number(q).toLocaleString(undefined, { maximumFractionDigits: 2 })}${uom ? ` ${uom}` : ''}`;
};

const LineTable = ({ rows, accent }) => (
  <Table>
    <TableHeader>
      <TableRow className="bg-muted/30">
        <TableHead className="text-[10px] font-semibold uppercase w-[90px]">Meter</TableHead>
        <TableHead className="text-[10px] font-semibold uppercase">Line item</TableHead>
        <TableHead className="text-[10px] font-semibold uppercase text-right">Quantity</TableHead>
        <TableHead className="text-[10px] font-semibold uppercase text-right">Rate</TableHead>
        <TableHead className="text-[10px] font-semibold uppercase text-right">Amount</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {rows.map((ch, i) => (
        <TableRow key={i} className="text-xs" data-testid={`${accent}-line-${i}`}>
          <TableCell className="py-1 text-muted-foreground font-mono">{ch.meter_number || '\u2014'}</TableCell>
          <TableCell className="py-1">{ch.description || ch.line_item || ch.category || '\u2014'}</TableCell>
          <TableCell className="py-1 text-right font-mono">{fmtQty(ch.quantity, ch.quantity_uom)}</TableCell>
          <TableCell className="py-1 text-right text-muted-foreground font-mono">
            {ch.rate ? `${ch.rate_uom === '$' || !ch.rate_uom ? '$' : ''}${Number(ch.rate).toLocaleString(undefined, { maximumFractionDigits: 5 })}` : '\u2014'}
          </TableCell>
          <TableCell className="py-1 text-right font-mono">{fmt(ch.total)}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const UsageDemandTab = ({ bill }) => {
  const charges = bill.charges || [];
  const usage = charges.filter((c) => classifyLine(c) === 'usage');
  const demand = charges.filter((c) => classifyLine(c) === 'demand');

  // Headline metrics: total consumption by UOM + peak demand.
  const usageByUom = {};
  usage.forEach((c) => {
    const uom = (c.quantity_uom || '').toUpperCase();
    if (c.quantity) usageByUom[uom || 'UNITS'] = (usageByUom[uom || 'UNITS'] || 0) + Number(c.quantity);
  });
  // Fall back to denormalized usage fields when charge lines lack quantities.
  if (!Object.keys(usageByUom).length) {
    if (bill._dn_usage_kwh) usageByUom.KWH = bill._dn_usage_kwh;
    if (bill._dn_usage_therms) usageByUom.THERMS = bill._dn_usage_therms;
    if (bill._dn_usage_gallons) usageByUom.GALLONS = bill._dn_usage_gallons;
  }
  const peakDemand = demand.reduce((m, c) => Math.max(m, Number(c.quantity) || 0), 0);
  const demandTotal = demand.reduce((s, c) => s + (Number(c.total) || 0), 0);
  const usageTotal = usage.reduce((s, c) => s + (Number(c.total) || 0), 0);

  if (!usage.length && !demand.length && !Object.keys(usageByUom).length) {
    return <div className="p-8 text-center text-sm text-muted-foreground" data-testid="usage-demand-empty">
      No usage or demand line items on this bill.
    </div>;
  }

  return (
    <div className="p-4 space-y-5" data-testid="usage-demand-tab">
      {/* Headline metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Object.entries(usageByUom).map(([uom, qty]) => (
          <div key={uom} className="rounded-lg border bg-card p-3" data-testid={`usage-metric-${uom}`}>
            <div className="text-[10px] uppercase text-muted-foreground tracking-wide">Total {uom}</div>
            <div className="text-lg font-semibold font-mono">{Number(qty).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
          </div>
        ))}
        {peakDemand > 0 && (
          <div className="rounded-lg border bg-sky-50 border-sky-200 p-3" data-testid="demand-metric-peak">
            <div className="text-[10px] uppercase text-sky-700 tracking-wide">Peak Demand</div>
            <div className="text-lg font-semibold font-mono text-sky-800">{peakDemand.toLocaleString()} kW</div>
          </div>
        )}
      </div>

      {/* Usage lines */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-700">Usage</Badge>
          <span className="text-xs font-mono font-semibold">{fmt(usageTotal)}</span>
        </div>
        {usage.length ? <LineTable rows={usage} accent="usage" />
          : <p className="text-xs text-muted-foreground py-2">No itemized usage charges (see headline totals above).</p>}
      </div>

      {/* Demand lines */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Badge variant="outline" className="text-xs bg-sky-500/10 text-sky-700">Demand</Badge>
          <span className="text-xs font-mono font-semibold">{fmt(demandTotal)}</span>
        </div>
        {demand.length ? <LineTable rows={demand} accent="demand" />
          : <p className="text-xs text-muted-foreground py-2">No demand (kW) charges on this bill.</p>}
      </div>
    </div>
  );
};

/* ───── Charges Tab ──────────────────── */
const ChargesTab = ({ summary }) => {
  if (!summary || !summary.categories?.length) return <div className="p-8 text-center text-sm text-muted-foreground">No charge breakdown data available</div>;
  const COLORS = { Usage: 'bg-blue-500/10 text-blue-700', Supply: 'bg-teal-500/10 text-teal-700', Delivery: 'bg-indigo-500/10 text-indigo-700', Tax: 'bg-amber-500/10 text-amber-700', Regulatory: 'bg-orange-500/10 text-orange-700', Other: 'bg-slate-500/10 text-slate-700' };
  return (
    <div className="p-4 space-y-4">
      {summary.categories.map((cat) => (
        <div key={cat.category} className="space-y-1">
          <div className="flex items-center justify-between">
            <Badge variant="outline" className={`text-xs ${COLORS[cat.category] || COLORS.Other}`}>{cat.category}</Badge>
            <span className="text-xs font-mono font-semibold">{fmt(cat.total)}</span>
          </div>
          <Table>
            <TableBody>
              {cat.items.map((item, i) => (
                <TableRow key={i} className="text-xs">
                  <TableCell className="py-1 text-muted-foreground w-[100px] font-mono">{item.meter_number || '\u2014'}</TableCell>
                  <TableCell className="py-1">{item.description || item.line_item}</TableCell>
                  <TableCell className="py-1 text-right text-muted-foreground">{item.quantity ? `${item.quantity} ${item.quantity_uom || ''}` : ''}</TableCell>
                  <TableCell className="py-1 text-right font-mono">{fmt(item.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ))}
      <div className="flex justify-between pt-3 border-t text-sm font-semibold">
        <span>Total Charges</span><span className="font-mono">{fmt(summary.total_charges)}</span>
      </div>
    </div>
  );
};

/* ───── Transactions Tab ─────────────── */
const TransactionsTab = ({ billId, accountId }) => {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!billId || !accountId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/payments/v2/bill-transactions/${billId}`, { params: { account_id: accountId } })
      .then(({ data }) => { if (!cancelled) setTxns(data?.transactions || []); })
      .catch(() => { if (!cancelled) setTxns([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [billId, accountId]);

  if (loading) {
    return <div className="p-6 text-center text-xs text-muted-foreground"><Loader2 className="h-4 w-4 inline animate-spin mr-2" />Loading transactions…</div>;
  }
  if (!txns.length) return <div className="p-8 text-center text-sm text-muted-foreground">No payment or refund transactions recorded</div>;
  return (
    <div className="p-4">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30">
            <TableHead className="text-[10px] font-semibold uppercase">Date</TableHead>
            <TableHead className="text-[10px] font-semibold uppercase">Paid By</TableHead>
            <TableHead className="text-[10px] font-semibold uppercase text-right">Amount</TableHead>
            <TableHead className="text-[10px] font-semibold uppercase text-right">Service Fee</TableHead>
            <TableHead className="text-[10px] font-semibold uppercase">Reference</TableHead>
            <TableHead className="text-[10px] font-semibold uppercase">Method</TableHead>
            <TableHead className="text-[10px] font-semibold uppercase">Notes</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {txns.map((tx, i) => (
            <TableRow key={tx.id || i} data-testid={`bill-detail-txn-row-${i}`}>
              <TableCell className="text-xs">{fmtDate(tx.transaction_date || tx.created_at)}</TableCell>
              <TableCell className="text-xs">
                <Badge variant="outline" className="text-[10px]">{tx.paid_by_name || '\u2014'}</Badge>
              </TableCell>
              <TableCell className="text-xs text-right font-mono font-semibold text-emerald-700">{fmt(tx.amount)}</TableCell>
              <TableCell className="text-xs text-right font-mono">{tx.payment_service_charge > 0 ? fmt(tx.payment_service_charge) : '\u2014'}</TableCell>
              <TableCell className="text-xs font-mono text-muted-foreground">{tx.transaction_reference || '\u2014'}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{tx.payment_method || '\u2014'}</TableCell>
              <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate" title={tx.notes}>{tx.notes || '\u2014'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
